/* eslint-disable class-methods-use-this */
import CustomViewApi from "services/api/customView/CustomViewApi";
import MailboxesApi from "services/api/mailboxes/MailboxesApi";
import { AppStateProvider } from "services/app-state-provider";
import { getAccessToken, getAuthKey, getCurrentAgentId } from "store/features/session/selectors";

import AccountsBillingV3Api from "./accounts-billing-v3/accounts-billing-v3-api";
import AddressesApi from "./addresses/addresses-api";
import { AgentApi } from "./agent";
import ApiClient, { IApiClient } from "./api-client";
import { ApplicationSettingsApi } from "./application-settings/application-settings-api";
import { ApplicationsApi } from "./applications";
import { AttachmentApi } from "./attachment";
import AuditLogApi from "./audit-log/audit-log";
import { BillingApi } from "./billing";
import { CannedResponsesApi } from "./canned-responses";
import { ClientSsoApi } from "./client-sso";
import { ContactFormsApi } from "./contact-forms";
import { CustomFieldApi } from "./custom-fields";
import { EmailDomainApi } from "./email-domain";
import { EmailTemplateApi } from "./email-templates/email-template-api";
import { TextEnhancementsApi } from "./enhancements/text-enhancements";
import { ExportApi } from "./export";
import GlobalAccountsApi from "./global-accounts/global-accounts-api";
import { IntegrationsApi } from "./integrations";
import { InvoiceApi } from "./invoice";
import { KnowledgeBaseApi } from "./knowledgebase";
import { LicenseApi } from "./license";
import { LivechatApi } from "./livechat";
import MacroApi from "./macros/macro-api";
import { PreviewApi } from "./preview";
import { ReplyAddressApi } from "./reply-addresses/reply-address-api";
import { ReportApi } from "./report";
import RuleApi from "./rules/rule-api";
import SpamApi from "./spam/spam-api";
import { SubscriptionApi } from "./subscription";
import { TagApi } from "./tag";
import { TeamApi } from "./team";
import { TicketApi } from "./ticket";
import { TimezoneApi } from "./timezone";
import { TransactionsApi } from "./transactions/transaction-api";
import { VideoApi } from "./video";
import { WebsiteApi } from "./website";

interface IBaseApiConstructor<T> {
  new (api: IApiClient): T;
}

type ApiType =
  | AgentApi
  | ApplicationsApi
  | AttachmentApi
  | BillingApi
  | ClientSsoApi
  | EmailDomainApi
  | InvoiceApi
  | IntegrationsApi
  | LicenseApi
  | MailboxesApi
  | PreviewApi
  | SubscriptionApi
  | TagApi
  | TeamApi
  | TicketApi
  | CustomViewApi
  | WebsiteApi
  | CannedResponsesApi
  | TransactionsApi
  | ReportApi
  | ExportApi
  | ReplyAddressApi
  | RuleApi
  | EmailTemplateApi
  | ApplicationSettingsApi
  | GlobalAccountsApi
  | AccountsBillingV3Api
  | MacroApi
  | VideoApi
  | TimezoneApi
  | ContactFormsApi
  | SpamApi
  | LivechatApi
  | KnowledgeBaseApi
  | CustomFieldApi
  | AuditLogApi
  | TextEnhancementsApi
  | AddressesApi;

export default class ApiManagerBase {
  private apiClient: ApiClient;
  private apiInstances: ApiType[] = [];

  constructor() {
    // @ts-expect-error TS(2345): Argument of type 'string | null | undefined' is no... Remove this comment to see the full error message
    this.apiClient = new ApiClient(getAccessToken(this.state));
    this.subscribeToStoreChanges();
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private get state() {
    return AppStateProvider.getState();
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private handleStoreChange = () => {
    // @ts-expect-error TS(2345): Argument of type 'string | null | undefined' is no... Remove this comment to see the full error message
    this.apiClient.updateAccessToken(getAccessToken(this.state));
    // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
    this.apiClient.setAuthKey(getAuthKey(this.state));
    // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
    this.apiClient.setCurrentAgentId(getCurrentAgentId(this.state));
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private subscribeToStoreChanges = () => {
    AppStateProvider.subscribe(this.handleStoreChange);
  };

  private getApiInstance = (TypeConstructor: IBaseApiConstructor<ApiType>): ApiType => {
    let instance = this.apiInstances.find(ai => ai instanceof TypeConstructor);

    if (!instance) {
      instance = new TypeConstructor(this.apiClient);
      this.apiInstances.push(instance);
    }

    return instance;
  };

  get agentApi(): AgentApi {
    return this.getApiInstance(AgentApi) as AgentApi;
  }

  get attachmentApi(): AttachmentApi {
    return this.getApiInstance(AttachmentApi) as AttachmentApi;
  }

  get billingApi(): BillingApi {
    return this.getApiInstance(BillingApi) as BillingApi;
  }

  get cannedResponsesApi(): CannedResponsesApi {
    return this.getApiInstance(CannedResponsesApi) as CannedResponsesApi;
  }

  get clientSsoApi(): ClientSsoApi {
    return this.getApiInstance(ClientSsoApi) as ClientSsoApi;
  }

  get applicationsApi(): ApplicationsApi {
    return this.getApiInstance(ApplicationsApi) as ApplicationsApi;
  }

  get emailDomainApi(): EmailDomainApi {
    return this.getApiInstance(EmailDomainApi) as EmailDomainApi;
  }

  get invoiceApi(): InvoiceApi {
    return this.getApiInstance(InvoiceApi) as InvoiceApi;
  }

  get integrationsApi(): IntegrationsApi {
    return this.getApiInstance(IntegrationsApi) as IntegrationsApi;
  }

  get reportApi(): ReportApi {
    return this.getApiInstance(ReportApi) as ReportApi;
  }

  get licenseApi(): LicenseApi {
    return this.getApiInstance(LicenseApi) as LicenseApi;
  }

  get mailboxesApi(): MailboxesApi {
    return this.getApiInstance(MailboxesApi) as MailboxesApi;
  }

  get previewApi(): PreviewApi {
    return this.getApiInstance(PreviewApi) as PreviewApi;
  }

  get subscriptionApi(): SubscriptionApi {
    return this.getApiInstance(SubscriptionApi) as SubscriptionApi;
  }

  get tagApi(): TagApi {
    return this.getApiInstance(TagApi) as TagApi;
  }

  get teamApi(): TeamApi {
    return this.getApiInstance(TeamApi) as TeamApi;
  }

  get ticketApi(): TicketApi {
    return this.getApiInstance(TicketApi) as TicketApi;
  }

  get customViewApi(): CustomViewApi {
    return this.getApiInstance(CustomViewApi) as CustomViewApi;
  }

  get websiteApi(): WebsiteApi {
    return this.getApiInstance(WebsiteApi) as WebsiteApi;
  }

  get exportApi(): ExportApi {
    return this.getApiInstance(ExportApi) as ExportApi;
  }

  get transactionApi(): TransactionsApi {
    return this.getApiInstance(TransactionsApi) as TransactionsApi;
  }

  get replyAddressApi(): ReplyAddressApi {
    return this.getApiInstance(ReplyAddressApi) as ReplyAddressApi;
  }

  get ruleApi(): RuleApi {
    return this.getApiInstance(RuleApi) as RuleApi;
  }

  get emailTemplateApi(): EmailTemplateApi {
    return this.getApiInstance(EmailTemplateApi) as EmailTemplateApi;
  }

  get applicationSettingsApi(): ApplicationSettingsApi {
    return this.getApiInstance(ApplicationSettingsApi) as ApplicationSettingsApi;
  }

  get videoApi(): VideoApi {
    return this.getApiInstance(VideoApi) as VideoApi;
  }

  get timezoneApi(): TimezoneApi {
    return this.getApiInstance(TimezoneApi) as TimezoneApi;
  }

  get globalAccountsApi(): GlobalAccountsApi {
    return this.getApiInstance(GlobalAccountsApi) as GlobalAccountsApi;
  }

  get accountsBillingV3Api(): AccountsBillingV3Api {
    return this.getApiInstance(AccountsBillingV3Api) as AccountsBillingV3Api;
  }

  get macrosApi(): MacroApi {
    return this.getApiInstance(MacroApi) as MacroApi;
  }

  get spamApi(): SpamApi {
    return this.getApiInstance(SpamApi) as SpamApi;
  }

  get contactFormApi(): ContactFormsApi {
    return this.getApiInstance(ContactFormsApi) as ContactFormsApi;
  }

  get livechatApi(): LivechatApi {
    return this.getApiInstance(LivechatApi) as LivechatApi;
  }

  get knowledgebaseApi(): KnowledgeBaseApi {
    return this.getApiInstance(KnowledgeBaseApi) as KnowledgeBaseApi;
  }

  get customFieldsApi(): CustomFieldApi {
    return this.getApiInstance(CustomFieldApi) as CustomFieldApi;
  }

  get textEnhancersApi(): TextEnhancementsApi {
    return this.getApiInstance(TextEnhancementsApi) as TextEnhancementsApi;
  }

  get auditLogApi(): AuditLogApi {
    return this.getApiInstance(AuditLogApi) as AuditLogApi;
  }

  get addressesApi(): AddressesApi {
    return this.getApiInstance(AddressesApi) as AddressesApi;
  }
}
